import * as React from 'react'

import { createContext } from '~logic/lib/react'
import { SubmitCartItemProvider } from '~shared/segments/Cart'

import { useProductDetailStateUpdaters } from '../hooks/useProductDetailStateUpdaters'
import { useProductDetailStateValues } from '../hooks/useProductDetailStateValues'
import { useProductDetailCtx } from './ProductDetailProvider'

type TProductInList = Defined<ReturnType<typeof useProductDetailCtx>['product']>
type TProductDetail = Defined<ReturnType<typeof useProductDetailStateValues>['productDetail']>
type TProduct = TProductInList &
  RecursivePartial<TProductDetail> & {
    placeholderImage: TProductInList['image']
    allergens?: TProductDetail['allergens'] // recursive partial is not working as intended on nested arrays
  }

type TUpdateContext = ReturnType<typeof useProductDetailStateUpdaters>

type TProductContext = {
  product: TProduct
  isLoading: boolean
}

type TStateContext = Omit<ReturnType<typeof useProductDetailStateValues>, 'productDetail'>

const [UpdateProvider, useUpdateProductDetailCtx] =
  createContext<TUpdateContext>(`ProductDetailUpdate`)
const [ProductProvider, useProductDetailDataCtx] =
  createContext<TProductContext>(`ProductDetailData`)
const [StateProvider, useProductDetailStateCtx] = createContext<TStateContext>(`ProductDetailState`)

type TLanguage = FirstParam<typeof useProductDetailStateValues>['selectedLanguage']

type TProps = RequiredChildren & {
  /**
   * selected language of the app
   */
  selectedLanguage: TLanguage
}

// TODO rename ProductDetailData?
// TODO render CartItemSubmitProvider conditionally (omit for RO menu)

/**
 * Combined providers used by the ProductDetail components.
 * Use the `useUpdateProductDetail` to get ProductDetail state updaters (modifying note, selected addons, count).
 * Use the `useProductDetailData` to get static info about product that the detail is opened for.
 * Use the `useProductDetailState` to get live state of the ProductDetail.
 */
export const ProductDetailStateProvider: React.FC<TProps> = ({ selectedLanguage, children }) => {
  const { product } = useProductDetailCtx()
  const updaters = useProductDetailStateUpdaters()

  const { productDetail, ...state } = useProductDetailStateValues({ selectedLanguage })

  // combine addon detail info (once loaded) with the addon-in-list info
  const productDetailData = React.useMemo<Nullable<TProduct>>(() => {
    if (!product) {
      return null
    }

    // to comply with the nice image progressive enhancement logic
    // the smaller image url (from the product in the list) should be presented as placeholder image
    // and the image prop should be reserved only for the bigger version loaded with the product detail
    return productDetail && !state.isLoading
      ? { ...product, ...productDetail, placeholderImage: product.image }
      : { ...product, placeholderImage: product.image, image: null }
  }, [product, state.isLoading, productDetail])

  if (!productDetailData) {
    return null
  }

  return (
    <UpdateProvider value={updaters}>
      <SubmitCartItemProvider>
        <ProductProvider value={{ product: productDetailData, isLoading: state.isLoading }}>
          <StateProvider value={state}>{children}</StateProvider>
        </ProductProvider>
      </SubmitCartItemProvider>
    </UpdateProvider>
  )
}

export { useProductDetailStateCtx, useUpdateProductDetailCtx, useProductDetailDataCtx }
